// budgetOutlook — turning GET /budgets/{id}/risk into the questions a budget owner
// actually has to answer.
//
// A budget is an AUTHORIZATION, not an enforcement behaviour. So the cockpit's risk
// column is not a set of derived statistics; it answers, in order:
//
//   1. Are we still inside the authorization?      -> authorizationOutlook()
//   2. Where is the greatest risk to it?           -> riskFocus()
//   3. Can I trust the projection behind that?     -> the payload's confidence block
//
// Two rules drive everything here:
//
//   * Rank by MONEY AT RISK, never by |variance %|. The backend's `worstPct` picks the
//     largest absolute percentage, so a period forecast 63% UNDER its amount outranks one
//     projected 15% over — mathematically the biggest deviation, and from a control
//     standpoint the best outcome on the page. A period that is over is always more
//     important than any period that is under, and among the over ones the largest euro
//     overage wins.
//   * If every period is inside its amount, say so. Do not manufacture a "worst" period;
//     report the one CLOSEST TO ITS CAP instead, which is the real early warning.
//
// Everything is a pure function over the payload, so the rounding lives here too: raw
// backend floats (-16.336943446064833) must never reach a card.

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);

// round1 — one decimal, and never "-0".
export function round1(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const r = Math.round(v * 10) / 10;
  return r === 0 ? 0 : r;
}

// A period only counts when the backend actually covered it (`hasValue`) AND it carries an
// authorized amount. Periods with no amount set have nothing to be over or under.
function isMeasurable(p) {
  return (
    p != null &&
    p.hasValue !== false &&
    typeof p.value === "number" &&
    Number.isFinite(p.value) &&
    typeof p.planned === "number" &&
    Number.isFinite(p.planned) &&
    p.planned > 0
  );
}

export function measurablePeriods(risk) {
  const rows = risk?.variance?.perPeriod;
  if (!Array.isArray(rows)) return [];
  return rows.filter(isMeasurable);
}

// authorizationOutlook — "are we within authorization?"
//
// breach  : the projection crosses the authorized amount in a known period.
// clear   : no period is projected past its amount; reports the aggregate headroom.
// unknown : nothing measurable yet (new budget, no forecast) — say that rather than
//           implying safety.
export function authorizationOutlook(risk) {
  const breach = risk?.earliestBreach;
  if (breach && breach.period) {
    const cap = num(breach.cap);
    const overBy = num(breach.overBy);
    return {
      state: "breach",
      period: String(breach.period),
      authorized: cap,
      projected: num(breach.projected),
      overBy,
      overPct: cap > 0 ? round1((overBy / cap) * 100) : null,
      basis: breach.basis ?? null,
    };
  }

  const periods = measurablePeriods(risk);
  if (periods.length === 0) return { state: "unknown" };

  const authorized = periods.reduce((s, p) => s + p.planned, 0);
  const projected = periods.reduce((s, p) => s + p.value, 0);
  return {
    state: "clear",
    authorized,
    projected,
    headroom: authorized - projected,
    periodCount: periods.length,
    runwayThroughPeriod: risk?.runwayThroughPeriod || null,
  };
}

// riskFocus — "where is the greatest risk?"
//
// kind "over"   : at least one period is projected past its amount. The winner is the one
//                 with the largest euro overage — the money actually at risk.
// kind "within" : nothing is over. The winner is the period closest to its cap, reported
//                 as utilization + remaining headroom.
// null          : nothing measurable.
export function riskFocus(risk) {
  const periods = measurablePeriods(risk);
  if (periods.length === 0) return null;

  const over = periods.filter((p) => p.value > p.planned);
  const pool = over.length > 0 ? over : periods;

  const score = over.length > 0
    ? (p) => p.value - p.planned // money at risk
    : (p) => p.value / p.planned; // utilization

  let best = pool[0];
  for (const p of pool) if (score(p) > score(best)) best = p;

  const delta = best.value - best.planned;
  return {
    kind: over.length > 0 ? "over" : "within",
    period: String(best.period),
    authorized: best.planned,
    projected: best.value,
    delta, // signed: positive is over the authorized amount
    headroom: -delta,
    usedPct: round1((best.value / best.planned) * 100),
    basis: best.basis ?? null,
    overCount: over.length,
  };
}

// periodFinancialState — the financial state of ONE period row.
//
// Deliberately distinct from the period's lifecycle status (active / draft change /
// pending approval): one says whether the amount is agreed, this one says whether the
// projection fits inside it.
export function periodFinancialState(planned, projected) {
  if (
    typeof planned !== "number" ||
    !Number.isFinite(planned) ||
    planned <= 0 ||
    typeof projected !== "number" ||
    !Number.isFinite(projected)
  ) {
    return null;
  }
  const usedPct = round1((projected / planned) * 100);
  const delta = projected - planned;
  let state = "within";
  if (delta > 0) state = "over";
  else if (usedPct >= 90) state = "near";
  return { state, usedPct, delta, headroom: -delta };
}

// utilizationTone — how loudly a utilization figure should be rendered.
//
// A word on every row ("Near cap" five times in a column) stops being a signal and becomes
// texture. The percentage is already comparable at a glance — 88.8 → 92.2 → 95.9 → 99.3 —
// so the ladder is carried by colour, and prose is reserved for the one state that is a
// genuine exception.
//
//   under 90   neutral    nothing to say
//   90–95      watch      worth noticing, not worth colouring
//   95–100     attention  amber
//   100+       over       red, and the only state that gets a word
export function utilizationTone(usedPct) {
  if (typeof usedPct !== "number" || !Number.isFinite(usedPct)) return "neutral";
  if (usedPct >= 100) return "over";
  if (usedPct >= 95) return "attention";
  if (usedPct >= 90) return "watch";
  return "neutral";
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// formatPeriodRange — "Jun 2026 – May 2027": the span the authorization actually covers,
// stated once beside the amount instead of left for the reader to infer from the table.
//
// Reads the period KEY, not startDate/endDate: periods hydrated from the API carry only
// "2026-06" / "2026-Q2", and the ISO dates exist only on freshly generated ones. The dates
// are the fallback, not the source.
function shortPeriod(period, isoField) {
  const key = String(period?.label ?? period?.periodKey ?? period?.id ?? "");
  const month = /^(\d{4})-(\d{2})$/.exec(key);
  if (month) {
    const idx = Number(month[2]) - 1;
    if (idx >= 0 && idx <= 11) return `${MONTHS[idx].slice(0, 3)} ${month[1]}`;
  }
  const quarter = /^(\d{4})-Q([1-4])$/i.exec(key);
  if (quarter) return `Q${quarter[2]} ${quarter[1]}`;

  const iso = /^(\d{4})-(\d{2})/.exec(String(period?.[isoField] || ""));
  if (iso) {
    const idx = Number(iso[2]) - 1;
    if (idx >= 0 && idx <= 11) return `${MONTHS[idx].slice(0, 3)} ${iso[1]}`;
  }
  return null;
}

export function formatPeriodRange(periods) {
  if (!Array.isArray(periods) || periods.length === 0) return "";
  const first = shortPeriod(periods[0], "startDate");
  const last = shortPeriod(periods[periods.length - 1], "endDate");
  if (!first || !last) return "";
  return first === last ? first : `${first} – ${last}`;
}

// formatPeriodName — "2026-10" reads as a key, "October 2026" reads as a date. Used where a
// period is named in prose (the risk cards); the periods table keeps the raw key, which is
// what its rows are identified by.
export function formatPeriodName(label) {
  if (label == null) return "";
  const s = String(label);
  const month = /^(\d{4})-(\d{2})$/.exec(s);
  if (month) {
    const m = Number(month[2]);
    if (m >= 1 && m <= 12) return `${MONTHS[m - 1]} ${month[1]}`;
  }
  const quarter = /^(\d{4})-Q([1-4])$/i.exec(s);
  if (quarter) return `Q${quarter[2]} ${quarter[1]}`;
  return s;
}
